import { useState } from 'react'
import type { RouterInputs, RouterOutputs } from 'shared/api'
import { trpcClient } from 'shared/api'
import { saveRegisterFile, type Base64File } from 'shared/components/partner-registers/register-file'
import { Download } from 'lucide-react'
import { Hint, SectionTitle } from 'shared/components/section'
import {
	AlertDialog,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from 'shared/components/ui/alert-dialog'
import { Button } from 'shared/components/ui/button'
import { Input } from 'shared/components/ui/input'
import { Label } from 'shared/components/ui/label'
import { Textarea } from 'shared/components/ui/textarea'
import { useTranslate } from 'shared/lib/i18n'
import { formatDate } from 'shared/lib/utils/date'

/**
 * A Point on TezGo or not, on «Локации» (ADR-0045, Release 2 ticket 49; user stories
 * 145, 148).
 *
 *  - **Включить на TezGo** — from now the hub's scan makes the Point's Parcels TezGo's.
 *  - **Слив** — for the pilot: the parcels already on the Point's shelf in the old panel
 *    (`back`) are handed over there until it is empty. «Проверить» asks `back` — on the
 *    press, never by polling — «В старой панели на полке: N»; «Слив закончен» works only
 *    at 0.
 *  - **Откатить пункт** — the same act as switching it off: a preview that writes
 *    nothing (what is released — on the shelf, in Bags with drivers; what stays — at
 *    the hub, announced; what is in the way, each in words), then the confirmation with
 *    a comment. For a partner without a webhook (139) — «Скачать список 139».
 *
 * The state, the counts and the refusals are the server's; the calls are injected so
 * the screen is tested without a backend.
 */
type Location = RouterOutputs['locations']['List'][number]
export type DrainCheck = RouterOutputs['locations']['DrainCompletion']
export type RollbackPreview = RouterOutputs['locations']['RollbackPreview']
export type RollbackAnswer = RouterOutputs['locations']['Rollback']
export type ReleasedListFile = RouterOutputs['locations']['ReleasedList']

export interface PointSwitchCalls {
	switchOn: (input: RouterInputs['locations']['SetMigrated']) => Promise<unknown>
	setDrain: (input: RouterInputs['locations']['SetDrain']) => Promise<unknown>
	checkDrain: (input: RouterInputs['locations']['DrainCompletion']) => Promise<DrainCheck>
	endDrain: (input: RouterInputs['locations']['ClearDrain']) => Promise<unknown>
	preview: (input: RouterInputs['locations']['RollbackPreview']) => Promise<RollbackPreview>
	rollBack: (input: RouterInputs['locations']['Rollback']) => Promise<RollbackAnswer>
	releasedList: (input: RouterInputs['locations']['ReleasedList']) => Promise<ReleasedListFile>
	saveFile: (file: Base64File) => void
}

const viaTrpc: PointSwitchCalls = {
	switchOn: (input) => trpcClient.locations.SetMigrated.mutate(input),
	setDrain: (input) => trpcClient.locations.SetDrain.mutate(input),
	checkDrain: (input) => trpcClient.locations.DrainCompletion.query(input),
	endDrain: (input) => trpcClient.locations.ClearDrain.mutate(input),
	preview: (input) => trpcClient.locations.RollbackPreview.query(input),
	rollBack: (input) => trpcClient.locations.Rollback.mutate(input),
	releasedList: (input) => trpcClient.locations.ReleasedList.query(input),
	saveFile: saveRegisterFile,
}

const failure = (error: unknown) => (error instanceof Error ? error.message : String(error))

type Message = { ok: boolean; text: string }

export function PointSwitch({
	location,
	onChanged,
	calls = viaTrpc,
}: {
	location: Location
	onChanged?: () => void
	calls?: PointSwitchCalls
}) {
	const { t } = useTranslate()
	const [busy, setBusy] = useState(false)
	const [message, setMessage] = useState<Message | null>(null)
	const [drainUntil, setDrainUntil] = useState('')
	const [check, setCheck] = useState<DrainCheck | null>(null)
	const [preview, setPreview] = useState<RollbackPreview | null>(null)
	const [comment, setComment] = useState('')
	const [released, setReleased] = useState<{ names: string; count: number } | null>(null)
	const [rollingBack, setRollingBack] = useState(false)

	const run = async (act: () => Promise<Message | null>) => {
		if (busy) return
		setBusy(true)
		setMessage(null)
		try {
			const result = await act()
			if (result) setMessage(result)
		} catch (error) {
			setMessage({ ok: false, text: t('cutover.failed', { message: failure(error) }) })
		} finally {
			setBusy(false)
		}
	}

	const onSwitchOn = () =>
		run(async () => {
			await calls.switchOn({ _id: location._id, migrated: true })
			onChanged?.()
			return { ok: true, text: t('cutover.switchedOn') }
		})

	const onSetDrain = () =>
		run(async () => {
			await calls.setDrain({ _id: location._id, expectedEndDate: new Date(drainUntil) })
			setDrainUntil('')
			setCheck(null)
			onChanged?.()
			return { ok: true, text: t('cutover.drainSaved') }
		})

	const onCheck = () =>
		run(async () => {
			setCheck(await calls.checkDrain({ _id: location._id }))
			return null
		})

	const onEndDrain = () =>
		run(async () => {
			await calls.endDrain({ _id: location._id })
			setCheck(null)
			onChanged?.()
			return { ok: true, text: t('cutover.drainEnded') }
		})

	const onPreview = () =>
		run(async () => {
			setPreview(await calls.preview({ _id: location._id }))
			return null
		})

	const onRollBack = () => {
		if (!comment.trim()) {
			setMessage({ ok: false, text: t('pointRollback.commentRequired') })
			return
		}
		void run(async () => {
			const answer = await calls.rollBack({ _id: location._id, comment })
			if (answer.outcome === 'refused') {
				if (answer.preview) setPreview(answer.preview)
				return { ok: false, text: t(`rollbackRefusal.${answer.reason}`) }
			}
			setComment('')
			setPreview(null)
			setCheck(null)
			setRollingBack(false)
			setReleased(
				answer.withoutWebhook.length > 0
					? {
							names: answer.withoutWebhook.map((p) => p.name).join(', '),
							count: answer.withoutWebhook.reduce((n, p) => n + p.count, 0),
						}
					: null,
			)
			onChanged?.()
			return {
				ok: true,
				text: t(answer.outcome === 'rolled_back' ? 'pointRollback.done' : 'pointRollback.already', {
					total: String(answer.released.total),
					onShelf: String(answer.released.onShelf),
					inBags: String(answer.released.inBagsWithDrivers),
				}),
			}
		})
	}

	const onDownload = () =>
		run(async () => {
			calls.saveFile(await calls.releasedList({ _id: location._id }))
			return null
		})

	const draining = location.drain !== null
	const previewBlocks = !preview || preview.obstacles.length > 0 || preview.refusal !== null

	const startRollback = () => {
		setMessage(null)
		setComment('')
		setPreview(null)
		setRollingBack(true)
		onPreview()
	}

	const messageLine = message && (
		<p
			className={message.ok ? 'text-sm text-emerald-700 dark:text-emerald-400' : 'text-sm text-destructive'}
			role={message.ok ? 'status' : 'alert'}
			data-testid='point-switch-message'
		>
			{message.text}
		</p>
	)

	return (
		<section className='flex flex-col gap-3' data-testid='point-switch'>
			<SectionTitle
				title={t('cutover.title')}
				hint={location.migrated ? undefined : t('cutover.switchOnHint')}
				aside={
					!location.migrated &&
					!location.closedForGood && (
						<Button size='sm' disabled={busy} onClick={onSwitchOn} data-testid='point-switch-on'>
							{t('cutover.switchOn')}
						</Button>
					)
				}
			/>
			<p className='text-sm' data-testid='point-switch-state' data-on={location.migrated ? 'yes' : 'no'}>
				{location.migrated ? t('cutover.on') : t('cutover.off')}
			</p>

			{location.migrated && (
				<div className='flex flex-col gap-2' data-testid='point-drain'>
					<div className='flex items-center gap-1.5'>
						<span className='text-sm font-medium'>{t('cutover.drainTitle')}</span>
						<Hint>{t('cutover.drainHint')}</Hint>
					</div>
					{draining ? (
						<>
							<div className='flex flex-wrap items-center gap-2'>
								<span className='text-sm' data-testid='point-drain-until'>
									{t('cutover.drainActive', { date: formatDate(location.drain!.expectedEndDate) })}
								</span>
								<div className='ml-auto flex items-center gap-2'>
									<Button size='sm' variant='outline' disabled={busy} onClick={onCheck} data-testid='point-drain-check'>
										{t('cutover.drainCheck')}
									</Button>
									<Button
										size='sm'
										disabled={busy || !check?.clearOffered}
										onClick={onEndDrain}
										title={t('cutover.drainEndHint')}
										data-testid='point-drain-end'
									>
										{t('cutover.drainEnd')}
									</Button>
								</div>
							</div>
							{check && <DrainCheckView check={check} pointId={location.id} />}
						</>
					) : (
						<div className='flex flex-wrap items-center gap-2'>
							<Label htmlFor={`drain-until-${location._id}`} className='font-normal text-muted-foreground'>
								{t('cutover.drainSetLabel')}
							</Label>
							<Input
								id={`drain-until-${location._id}`}
								type='date'
								className='h-8 w-40'
								value={drainUntil}
								onChange={(e) => setDrainUntil(e.target.value)}
								data-testid='point-drain-date'
							/>
							<Button size='sm' variant='outline' disabled={busy || !drainUntil} onClick={onSetDrain} data-testid='point-drain-set'>
								{t('cutover.drainSet')}
							</Button>
						</div>
					)}
				</div>
			)}

			{location.migrated && (
				<div className='flex flex-wrap items-center gap-2' data-testid='point-rollback'>
					<span className='text-sm font-medium'>{t('pointRollback.title')}</span>
					<Hint>{t('pointRollback.hint')}</Hint>
					<Button
						size='sm'
						variant='destructive'
						className='ml-auto'
						disabled={busy}
						onClick={startRollback}
						data-testid='point-rollback-start'
					>
						{t('pointRollback.confirm')}
					</Button>
				</div>
			)}

			{released && (
				<div className='flex flex-wrap items-center gap-2' data-testid='point-released-list'>
					<Button size='sm' variant='outline' disabled={busy} onClick={onDownload} data-testid='point-released-download'>
						<Download />
						{t('pointRollback.download', { name: released.names, count: String(released.count) })}
					</Button>
					<Hint>{t('pointRollback.downloadHint')}</Hint>
				</div>
			)}

			{!rollingBack && messageLine}

			<AlertDialog open={rollingBack} onOpenChange={(open) => !open && !busy && setRollingBack(false)}>
				<AlertDialogContent className='data-[size=default]:sm:max-w-xl' data-testid='point-rollback-dialog'>
					<AlertDialogHeader>
						<AlertDialogTitle>{t('pointRollback.ask', { id: location.id })}</AlertDialogTitle>
						<AlertDialogDescription>{t('pointRollback.askHint')}</AlertDialogDescription>
					</AlertDialogHeader>
					<div className='flex flex-col gap-3'>
						{preview ? (
							<RollbackPreviewView preview={preview} />
						) : (
							<p className='text-sm text-muted-foreground'>{t('pointRollback.loading')}</p>
						)}
						<div className='flex flex-col gap-1.5'>
							<Label htmlFor={`rollback-comment-${location._id}`}>{t('pointRollback.comment')}</Label>
							<Textarea
								id={`rollback-comment-${location._id}`}
								data-testid='point-rollback-comment'
								value={comment}
								placeholder={t('pointRollback.commentPlaceholder')}
								onChange={(e) => {
									setComment(e.target.value)
									setMessage(null)
								}}
							/>
						</div>
						{messageLine}
					</div>
					<AlertDialogFooter>
						<Button variant='outline' className='sm:mr-auto' disabled={busy} onClick={onPreview} data-testid='point-rollback-preview'>
							{t('pointRollback.preview')}
						</Button>
						<AlertDialogCancel disabled={busy}>{t('pointRollback.cancel')}</AlertDialogCancel>
						<Button
							variant='destructive'
							disabled={busy || previewBlocks}
							onClick={onRollBack}
							data-testid='point-rollback-confirm'
						>
							{t('pointRollback.confirm')}
						</Button>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</section>
	)
}

/** What `back` said of the old shelf: how many, empty, or why it could not be asked. */
export function DrainCheckView({ check, pointId }: { check: DrainCheck; pointId: string }) {
	const { t } = useTranslate()
	const text =
		check.oldSide === 'occupied'
			? t('cutover.drainOnOldShelf', { count: String(check.oldSideCount ?? 0) })
			: check.oldSide === 'empty'
				? t('cutover.drainEmpty')
				: t(`cutover.drainUnknown_${check.unknownReason ?? 'back_unavailable'}`, { id: pointId })
	return (
		<p
			className={check.oldSide === 'empty' ? 'text-sm text-emerald-700 dark:text-emerald-400' : 'text-sm text-muted-foreground'}
			data-testid='point-drain-status'
			data-old-side={check.oldSide}
		>
			{text}
		</p>
	)
}

/** The preview of a rollback: what is released, what stays, who gets a file, what is in the way. */
export function RollbackPreviewView({ preview }: { preview: RollbackPreview }) {
	const { t } = useTranslate()
	const c = preview.counts
	return (
		<div className='flex flex-col gap-2 rounded-md border p-3 text-sm' data-testid='point-rollback-preview-view'>
			<ul className='flex flex-col gap-0.5' data-testid='point-rollback-counts'>
				<li data-count='onShelf'>{t('pointRollback.countOnShelf', { count: String(c.onShelf) })}</li>
				<li data-count='inBagsWithDrivers'>
					{t('pointRollback.countInBags', { count: String(c.inBagsWithDrivers) })}
					{preview.bagsWithDrivers.length > 0 && ` (${preview.bagsWithDrivers.join(', ')})`}
				</li>
				<li className='font-medium' data-count='total'>
					{t('pointRollback.countTotal', { count: String(c.total) })}
				</li>
				{c.atHub > 0 && <li data-count='atHub'>{t('pointRollback.countAtHub', { count: String(c.atHub) })}</li>}
				{c.expected > 0 && <li data-count='expected'>{t('pointRollback.countExpected', { count: String(c.expected) })}</li>}
			</ul>
			{preview.withoutWebhook.map((p) => (
				<div key={p.partnerId} className='text-muted-foreground' data-testid='point-rollback-without-webhook'>
					{t('pointRollback.withoutWebhook', { name: p.name, count: String(p.count) })}
				</div>
			))}
			{preview.refusal && (
				<p className='text-destructive' role='alert' data-testid='point-rollback-refusal'>
					{t(`rollbackRefusal.${preview.refusal}`)}
				</p>
			)}
			{preview.obstacles.length > 0 ? (
				<div className='flex flex-col gap-0.5 text-destructive' data-testid='point-rollback-obstacles'>
					<div className='font-medium'>{t('pointRollback.obstaclesTitle')}</div>
					<ul className='list-inside list-disc'>
						{preview.obstacles.map((o) => (
							<li key={o.kind} data-obstacle={o.kind}>
								{t(`rollbackObstacle.${o.kind}`, {
									count: String(o.count),
									amount: (o.amount ?? 0).toLocaleString('ru-RU'),
									codes: o.codes.join(', '),
								})}
							</li>
						))}
					</ul>
				</div>
			) : (
				<div className='text-emerald-700 dark:text-emerald-400' data-testid='point-rollback-no-obstacles'>
					{t('pointRollback.noObstacles')}
				</div>
			)}
		</div>
	)
}
